import styled, { css } from 'styled-components'

export const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as const

type Breakpoint = (typeof breakpoints)[number]

export const BreakpointStart: Record<Breakpoint, number> = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 1280,
  xl: 1400
}

const BreakpointGutterRem: Record<Breakpoint, number> = {
  xs: 0.5,
  sm: 0.75,
  md: 1,
  lg: 1.5,
  xl: 1.5
}

const BreakpointMarginRem: Record<Breakpoint, number> = {
  xs: 1,
  sm: 1,
  md: 1,
  lg: 1.5,
  xl: 2
}

export type FluidOrFixedConfig = Partial<Record<Breakpoint, 'fluid' | 'fixed'>>

const getFixedOrFluidDimension =
  (breakpoint: Breakpoint) => (config: FluidOrFixedConfig) => {
    if (breakpoint === 'xs') {
      return css`
        width: 100%;
      `
    }

    const latestDefinedBreakpoint = breakpoints
      .slice(0, breakpoints.indexOf(breakpoint) + 1)
      .reverse()
      .find((breakpointInList) => !!config[breakpointInList])

    const isFixed = latestDefinedBreakpoint
      ? config[latestDefinedBreakpoint] === 'fixed'
      : false

    return isFixed
      ? css`
          width: ${BreakpointStart[breakpoint]}px;
        `
      : css`
          width: 100%;
        `
  }

export const Grid = styled.div<FluidOrFixedConfig>`
  margin: auto;
  padding: 0 ${BreakpointMarginRem.xs + BreakpointGutterRem.xs / 2}rem;
  ${getFixedOrFluidDimension('xs')}

  @media (min-width: ${BreakpointStart.sm}px) {
    padding: 0 ${BreakpointMarginRem.sm + BreakpointGutterRem.sm / 2}rem;
    ${getFixedOrFluidDimension('sm')}
  }

  @media (min-width: ${BreakpointStart.md}px) {
    padding: 0 ${BreakpointMarginRem.md + BreakpointGutterRem.md / 2}rem;
    ${getFixedOrFluidDimension('md')}
  }

  @media (min-width: ${BreakpointStart.lg}px) {
    padding: 0 ${BreakpointMarginRem.lg + BreakpointGutterRem.lg / 2}rem;
    ${getFixedOrFluidDimension('lg')}
  }

  @media (min-width: ${BreakpointStart.xl}px) {
    padding: 0 ${BreakpointMarginRem.xl + BreakpointGutterRem.xl / 2}rem;
    ${getFixedOrFluidDimension('xl')}
  }
`

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -${BreakpointGutterRem.xs / 2}rem;

  @media (min-width: ${BreakpointStart.sm}px) {
    margin: 0 -${BreakpointGutterRem.sm / 2}rem;
  }

  @media (min-width: ${BreakpointStart.md}px) {
    margin: 0 -${BreakpointGutterRem.md / 2}rem;
  }

  @media (min-width: ${BreakpointStart.lg}px) {
    margin: 0 -${BreakpointGutterRem.lg / 2}rem;
  }

  @media (min-width: ${BreakpointStart.xl}px) {
    margin: 0 -${BreakpointGutterRem.xl / 2}rem;
  }
`

const BreakpointColumns: Record<Breakpoint, number> = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 12,
  xl: 12
}

const getDimensions = (
  columnSpan: number | 'auto' | undefined,
  breakpoint: Breakpoint
) => {
  const maxColumns = BreakpointColumns[breakpoint]

  if (columnSpan === undefined) {
    return false
  }

  if (columnSpan === 'auto' || columnSpan > maxColumns || columnSpan <= 0) {
    return css`
      width: auto;
    `
  }

  if (columnSpan === maxColumns) {
    return css`
      width: 100%;
    `
  }

  return css`
    width: ${(100 * columnSpan) / maxColumns}%;
  `
}

const getPadding = (breakpoint: Breakpoint) => css`
  padding: 0 ${BreakpointGutterRem[breakpoint] / 2}rem;
`

const getOffset = (offset: number | undefined, breakpoint: Breakpoint) => {
  const maxColumns = BreakpointColumns[breakpoint]

  return offset === undefined || offset >= maxColumns || offset < 0
    ? false
    : css`
        margin-left: ${(100 * offset) / maxColumns}%;
      `
}

type ColProps = Partial<
  Record<Breakpoint, number | 'auto'> & {
    offset: Partial<Record<Breakpoint, number>>
  }
>

export const Col = styled.div.withConfig({
  shouldForwardProp: (prop) => !['offset', ...breakpoints].includes(prop)
})<ColProps>`
  max-width: 100%;
  width: 100%;
  flex: 0 0 auto;

  ${({ xs }) => getDimensions(xs, 'xs')}
  ${getPadding('xs')}
  ${({ offset }) => getOffset(offset?.xs, 'xs')}

  @media (min-width: ${BreakpointStart.sm}px) {
    ${({ sm }) => getDimensions(sm, 'sm')}
    ${getPadding('sm')}
    ${({ offset }) => getOffset(offset?.sm, 'sm')}
  }

  @media (min-width: ${BreakpointStart.md}px) {
    ${({ md }) => getDimensions(md, 'md')}
    ${getPadding('md')}
    ${({ offset }) => getOffset(offset?.md, 'md')}
  }

  @media (min-width: ${BreakpointStart.lg}px) {
    ${({ lg }) => getDimensions(lg, 'lg')}
    ${getPadding('lg')}
    ${({ offset }) => getOffset(offset?.lg, 'lg')}
  }

  @media (min-width: ${BreakpointStart.xl}px) {
    ${({ xl }) => getDimensions(xl, 'xl')}
    ${getPadding('xl')}
    ${({ offset }) => getOffset(offset?.xl, 'xl')}
  }
`
